import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useLiveMatch } from '../LiveMatchContext.tsx';
import { LivePlayer } from '../liveMatchTypes.ts';
import ShirtColorPicker from './ShirtColorPicker.tsx';
import ShirtNumberPicker from './ShirtNumberPicker.tsx';

interface Props {
  onConfirm: () => void;
}

export default function CallupSelectionStep({ onConfirm }: Props) {
  const { state, dispatch } = useLiveMatch();
  const [tab, setTab] = useState<'HOME' | 'AWAY'>('HOME');

  // Mappa playerId → numero scelto manualmente (separata per lato)
  // numeri già noti (maglia in rosa, avversari generici delle amichevoli) precompilati e modificabili
  const knownNumbers = (list: LivePlayer[]) =>
    new Map(list.filter((p) => typeof p.shirtNumber === 'number' && p.shirtNumber >= 0).map((p) => [p.id, p.shirtNumber]));
  const [manualHome, setManualHome] = useState<Map<number, number>>(() => knownNumbers(state.homeAllPlayers));
  const [manualAway, setManualAway] = useState<Map<number, number>>(() => knownNumbers(state.awayAllPlayers));

  // Indice nella callup del giocatore corrente nel picker (null = picker chiuso)
  const [pickerIndex, setPickerIndex] = useState<number | null>(null);

  const players = tab === 'HOME' ? state.homeAllPlayers : state.awayAllPlayers;
  const callup = tab === 'HOME' ? state.homeCallup : state.awayCallup;
  const callupIds = new Set(callup.map((p) => p.id));

  const isOurTeam = tab === state.ourTeamSide;
  const teamName = tab === 'HOME' ? state.homeTeamName : state.awayTeamName;
  const shirtColor = tab === 'HOME' ? state.homeShirtColor : state.awayShirtColor;

  const manualNumbers = tab === 'HOME' ? manualHome : manualAway;
  const setManualNumbers = tab === 'HOME' ? setManualHome : setManualAway;

  function togglePlayer(player: LivePlayer) {
    const updated = callupIds.has(player.id)
      ? callup.filter((p) => p.id !== player.id)
      : [...callup, player];
    dispatch({ type: 'SET_CALLUP', side: tab, players: updated });
  }

  function selectAll() {
    dispatch({ type: 'SET_CALLUP', side: tab, players: [...players] });
  }

  function clearAll() {
    dispatch({ type: 'SET_CALLUP', side: tab, players: [] });
  }

  function openPicker(player: LivePlayer, e: React.MouseEvent) {
    e.stopPropagation();
    const idx = callup.findIndex((p) => p.id === player.id);
    if (idx !== -1) setPickerIndex(idx);
  }

  function handleNumberSelect(n: number) {
    if (pickerIndex === null) return;
    const player = callup[pickerIndex];
    if (!player) return;
    setManualNumbers((prev) => new Map(prev).set(player.id, n));
    dispatch({ type: 'UPDATE_SHIRT_NUMBER', side: tab, playerId: player.id, shirtNumber: n });
    // Non chiude il dialog: l'utente naviga con Prev/Next
  }

  function handlePickerPrev() {
    if (pickerIndex === null || callup.length === 0) return;
    setPickerIndex((i) => (i! - 1 + callup.length) % callup.length);
  }

  function handlePickerNext() {
    if (pickerIndex === null || callup.length === 0) return;
    setPickerIndex((i) => (i! + 1) % callup.length);
  }

  // Numeri già assegnati manualmente ad ALTRI convocati della squadra corrente
  function takenNumbers(excludePlayerId: number): Set<number> {
    const taken = new Set<number>();
    manualNumbers.forEach((num, pid) => {
      if (pid !== excludePlayerId) taken.add(num);
    });
    return taken;
  }

  const canConfirm = state.homeCallup.length >= 6 && state.awayCallup.length >= 6;

  return (
    <Box sx={{ p: 2 }}>
      {/* Tab squadre */}
      <Stack direction="row" spacing={1} mb={2}>
        {(['HOME', 'AWAY'] as const).map((side) => {
          const name = side === 'HOME' ? state.homeTeamName : state.awayTeamName;
          const cnt = side === 'HOME' ? state.homeCallup.length : state.awayCallup.length;
          return (
            <Chip
              key={side}
              label={`${name} (${cnt})`}
              onClick={() => setTab(side)}
              color={tab === side ? 'primary' : 'default'}
              variant={tab === side ? 'filled' : 'outlined'}
            />
          );
        })}
      </Stack>

      {/* Header squadra corrente */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle1" fontWeight="bold">
          {teamName}
          {isOurTeam && (
            <Chip label="La nostra squadra" size="small" color="success" sx={{ ml: 1 }} />
          )}
        </Typography>
        <ShirtColorPicker
          color={shirtColor}
          onChange={(c) => dispatch({ type: 'SET_SHIRT_COLOR', side: tab, color: c })}
        />
      </Stack>

      {/* Azioni di massa */}
      <Stack direction="row" spacing={1} mb={1.5}>
        <Button size="small" onClick={selectAll}>Seleziona tutti</Button>
        <Button size="small" color="inherit" onClick={clearAll}>Deseleziona tutti</Button>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
          {callup.length} convocati
        </Typography>
      </Stack>

      <Divider sx={{ mb: 1.5 }} />

      {/* Griglia giocatori */}
      <Grid container spacing={1}>
        {players.map((player) => {
          const selected = callupIds.has(player.id);
          const manualNum = manualNumbers.get(player.id) ?? null;
          const hasNumber = manualNum !== null;

          return (
            <Grid item xs={6} sm={4} md={3} key={player.id}>
              <Box
                onClick={() => togglePlayer(player)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  borderRadius: 1,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: selected ? 'primary.main' : 'divider',
                  bgcolor: selected ? 'primary.50' : 'background.paper',
                  '&:hover': { bgcolor: selected ? 'primary.100' : 'action.hover' },
                  '&:hover .edit-btn': { opacity: 1 },
                }}
              >
                {/* Avatar numero maglia — cliccabile solo se convocato */}
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                  <Avatar
                    sx={{
                      width: 38,
                      height: 38,
                      bgcolor: shirtColor,
                      fontSize: 13,
                      fontWeight: 'bold',
                      border: selected && !hasNumber ? '2px dashed' : 'none',
                      borderColor: 'warning.main',
                    }}
                  >
                    {hasNumber ? manualNum : '?'}
                  </Avatar>
                  {/* Icona modifica — visibile solo su hover e se convocato */}
                  {selected && (
                    <Box
                      className="edit-btn"
                      onClick={(e) => openPicker(player, e)}
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        bgcolor: 'rgba(0,0,0,0.45)',
                        opacity: 0,
                        transition: 'opacity 0.15s',
                        cursor: 'pointer',
                      }}
                    >
                      <EditIcon sx={{ fontSize: 16, color: 'white' }} />
                    </Box>
                  )}
                </Box>

                {/* Nome e numero */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={selected ? 'bold' : 'normal'} noWrap>
                    {player.firstName} {player.lastName}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={selected && !hasNumber ? 'warning.main' : 'text.secondary'}
                    noWrap
                  >
                    {hasNumber ? `#${manualNum}` : selected ? 'nessun numero' : '—'}
                    {player.roles.length > 0 && ` · ${player.roles.join(', ')}`}
                  </Typography>
                </Box>

                {selected
                  ? <CheckCircleIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
                  : <RadioButtonUncheckedIcon fontSize="small" color="disabled" sx={{ flexShrink: 0 }} />}
              </Box>
            </Grid>
          );
        })}

        {players.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
              Nessun giocatore disponibile
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* Bottone avanti */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title={canConfirm ? '' : 'Seleziona almeno 6 giocatori per squadra'}>
          <span>
            <Button variant="contained" disabled={!canConfirm} onClick={onConfirm}>
              Avanti →
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Picker numero maglia con navigazione */}
      {pickerIndex !== null && callup[pickerIndex] && (() => {
        const p = callup[pickerIndex];
        return (
          <ShirtNumberPicker
            open
            playerName={`${p.firstName} ${p.lastName}`}
            playerIndex={pickerIndex}
            totalPlayers={callup.length}
            currentNumber={manualNumbers.get(p.id) ?? null}
            takenNumbers={takenNumbers(p.id)}
            onSelect={handleNumberSelect}
            onPrev={handlePickerPrev}
            onNext={handlePickerNext}
            onClose={() => setPickerIndex(null)}
          />
        );
      })()}
    </Box>
  );
}
